import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOMetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: object;
}

const BASE_URL = 'https://web-nxs.lovable.app';

// SEO configurations optimized for corporate/enterprise keywords
const seoConfigs: Record<string, { title: string; description: string; keywords: string }> = {
  '/': {
    title: 'El ecosistema de reclutamiento para empresas #1 en Costa Rica',
    description: 'El ecosistema de reclutamiento para empresas #1 en Costa Rica. ATS + psicometría + bolsa de empleo: contrate 50% más rápido. +500 empresas confían en Nexus HR.',
    keywords: 'reclutamiento Costa Rica, selección personal, ATS, psicometría, hunting ejecutivo, recursos humanos'
  },
  '/nexus-ats': {
    title: 'Nexus ATS | Software de Reclutamiento con IA en Costa Rica',
    description: 'Publique en +17 job boards con un clic, filtre candidatos con IA y gestione todo su pipeline de reclutamiento desde una sola plataforma.',
    keywords: 'ATS software, applicant tracking system, sistema seguimiento candidatos, software reclutamiento, gestión vacantes, automatización HR'
  },
  '/reclutamiento-seleccion-meta': {
    title: 'Reclutamiento y Selección | Nexus HR Costa Rica',
    description: 'Nexus HR gestiona todo su proceso de reclutamiento. Candidatos filtrados con IA y psicometría listos para entrevistar. Proceso en 21 días promedio.',
    keywords: 'reclutamiento, selección, headhunting, Costa Rica'
  },
  '/candidatos': {
    title: 'Candidatos | Herramientas para profesionales | Nexus HR',
    description: 'Impulsá tu carrera con las herramientas gratuitas de Nexus HR: registro de CV, Nexus Lab, calculadora salarial y orientación vocacional.',
    keywords: 'candidatos costa rica, empleo, cv, calculadora salarial, orientación vocacional'
  },
  '/elempleo': {
    title: 'Elempleo.com | Bolsa de Empleo #1 de Costa Rica',
    description: 'Acceso a +1,000,000 candidatos activos. Publique vacantes y reciba aplicaciones calificadas en menos de 24 horas.',
    keywords: 'portal empleo, publicar vacantes, bolsa trabajo, elempleo Costa Rica, buscar candidatos, ofertas laborales'
  },
  '/pruebas-psicometricas': {
    title: 'Pruebas Psicométricas para Empresas | Nexus HR',
    description: 'Evalúe competencias, personalidad y potencial con Kokoro, Psigma Corp y Nexus Assessment. Reduzca el error de contratación hasta un 40%.',
    keywords: 'pruebas psicométricas laborales, test personalidad trabajo, evaluación competencias, assessment center, pruebas selección personal'
  },
  '/reclutamiento-seleccion': {
    title: 'Reclutamiento y Selección | Nexus HR Costa Rica',
    description: 'Servicio especializado de headhunting. Candidatos filtrados con IA y psicometría, listos para entrevistar en 21 días promedio.',
    keywords: 'reclutamiento ejecutivo, headhunting, selección personal, búsqueda talento, consultora recursos humanos, reclutamiento especializado'
  },
  '/encuesta-salarial': {
    title: 'Encuesta Salarial Costa Rica | Benchmarks y Tendencias 2025',
    description: 'Accede a datos salariales actualizados por industria y cargo. Toma decisiones de compensación basadas en el mercado. Informe personalizado.',
    keywords: 'encuesta salarial Costa Rica, benchmarks salariales, tendencias compensación, estudio remuneración, salarios por industria'
  },
  '/talent-connect': {
    title: 'Talent Connect | Base de Talento Ejecutivo Calificado',
    description: 'Accede a +50,000 profesionales pre-evaluados. Perfiles ejecutivos, gerenciales y técnicos verificados. Contrata en la mitad del tiempo.',
    keywords: 'base datos candidatos, talent pool, profesionales calificados, perfiles ejecutivos, candidatos pre-evaluados'
  },
  '/calculadora-salarial': {
    title: 'Calculadora Salarial | Conoce tu Salario de Mercado',
    description: 'Calcula tu salario ideal según experiencia, cargo e industria. Basado en datos reales del mercado laboral costarricense. 100% gratis.',
    keywords: 'calculadora salarial, cuanto debería ganar, salario por cargo, comparar salario, valor mercado profesional'
  },
  '/orientacion-vocacional': {
    title: 'Orienta360 | Orientación Vocacional con IA',
    description: 'Descubre tu carrera ideal con inteligencia artificial. Evaluación de intereses, habilidades y personalidad. Recomendaciones personalizadas.',
    keywords: 'orientación vocacional, test vocacional, elegir carrera, descubrir vocación, consejería profesional'
  },
  '/nexus-lab': {
    title: 'Nexus Lab | Creador de CV Profesional con IA',
    description: 'Crea un CV que destaque con ayuda de inteligencia artificial. Plantillas ATS-friendly. Optimizado para sistemas de reclutamiento. Gratis.',
    keywords: 'crear CV, curriculum vitae, generador CV, plantillas CV, hoja vida profesional, CV inteligencia artificial'
  },
  '/registrar-cv': {
    title: 'Registrar Hoja de Vida | Únete a +50,000 Profesionales',
    description: 'Sube tu CV y sé encontrado por las mejores empresas de Costa Rica. Acceso a oportunidades exclusivas. Registro 100% gratis.',
    keywords: 'registrar CV, subir hoja vida, buscar empleo, base candidatos, encontrar trabajo, oportunidades laborales'
  },
  '/ofertas-empleo': {
    title: 'Ofertas de Empleo Costa Rica | Vacantes Actualizadas',
    description: 'Explora cientos de vacantes en las mejores empresas. Posiciones ejecutivas, técnicas y operativas. Aplica con un clic.',
    keywords: 'ofertas empleo, vacantes trabajo, buscar empleo Costa Rica, oportunidades laborales, bolsa trabajo'
  },
  '/blog': {
    title: 'Blog HR | Tendencias en Reclutamiento y Recursos Humanos',
    description: 'Artículos sobre reclutamiento, selección de personal, tendencias HR y gestión del talento. Contenido para profesionales de RRHH.',
    keywords: 'blog recursos humanos, tendencias reclutamiento, gestión talento, artículos HR, noticias empleo'
  },
  '/nosotros': {
    title: 'Sobre Nexus HR | Casos de Éxito y Trayectoria',
    description: '+500 empresas confían en nosotros. Conoce nuestra historia, casos de éxito y por qué somos líderes en tecnología de reclutamiento.',
    keywords: 'sobre nexus hr, casos éxito reclutamiento, empresa recursos humanos, consultora HR Costa Rica'
  },
  '/sobre-nosotros': {
    title: 'Sobre Nexus HR | Casos de Éxito y Trayectoria',
    description: '+500 empresas confían en nosotros. Conoce nuestra historia, casos de éxito y por qué somos líderes en tecnología de reclutamiento.',
    keywords: 'sobre nexus hr, casos éxito reclutamiento, empresa recursos humanos, consultora HR Costa Rica'
  },
  '/ecosistema': {
    title: 'Ecosistema Nexus HR | Soluciones Integrales de Talento',
    description: 'Descubre cómo nuestras soluciones trabajan juntas para optimizar todo el ciclo de vida del talento en tu organización.',
    keywords: 'ecosistema HR, soluciones integradas, gestión talento integral, plataforma recursos humanos'
  },
  '/auth': {
    title: 'Iniciar Sesión | Nexus HR',
    description: 'Accede a tu cuenta de Nexus HR para gestionar reclutamiento, ver candidatos y administrar vacantes.',
    keywords: 'login Nexus HR, acceder cuenta, iniciar sesión'
  },
  '/politica-de-privacidad': {
    title: 'Política de Privacidad | Nexus HR',
    description: 'Conoce cómo Nexus HR protege y gestiona tus datos personales conforme a la legislación costarricense.',
    keywords: 'política privacidad, protección datos, Nexus HR, privacidad'
  },
  '/terminos-y-condiciones': {
    title: 'Términos y Condiciones | Nexus HR',
    description: 'Términos y condiciones de uso de los servicios de Nexus HR.',
    keywords: 'términos condiciones, uso servicios, Nexus HR'
  },
  '/descargables': {
    title: 'Descargables Gratuitos | Guías y Plantillas HR | Nexus HR',
    description: 'Descarga gratis guías de reclutamiento, plantillas de CV, checklists de onboarding y más recursos de RRHH.',
    keywords: 'descargables HR, plantillas CV, guías reclutamiento, checklist onboarding'
  },
  '/como-funciona': {
    title: 'Cómo funciona Nexus HR | Reclutamiento con IA paso a paso',
    description: 'Conocé el proceso de Nexus HR: ATS + IA + psicometría en 4 pasos. Publicá, filtrá, evaluá y contratá con datos reales.',
    keywords: 'cómo funciona, proceso reclutamiento, pasos ATS, metodología Nexus HR'
  },
  '/contacto': {
    title: 'Contacto | Solicitar consultoría con Nexus HR',
    description: 'Hablá con nuestro equipo y solicitá una consultoría gratuita. Te contactamos en menos de 24 horas hábiles.',
    keywords: 'contacto, solicitar consultoría, asesoría reclutamiento Costa Rica'
  },
  '/casos-de-exito': {
    title: 'Casos de Éxito | Empresas que confían en Nexus HR',
    description: '+500 empresas han transformado su reclutamiento con Nexus HR. Resultados reales en tiempo de contratación y retención.',
    keywords: 'casos éxito, clientes Nexus HR, resultados reclutamiento Costa Rica'
  },
  '/industrias': {
    title: 'Industrias | Soluciones de reclutamiento por sector | Nexus HR',
    description: 'Atendemos +8 industrias: banca, tecnología, retail, manufactura, salud, educación, logística y servicios profesionales.',
    keywords: 'reclutamiento por industria, sectores, hunting banca tecnología retail Costa Rica'
  }
};

const SEOMetaTags: React.FC<SEOMetaTagsProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = `${BASE_URL}/lovable-uploads/a2e4d3de-cc00-4222-a473-67c9a76aed45.png`,
  jsonLd
}) => {
  const location = useLocation();
  const path = location.pathname;
  const config = seoConfigs[path] || seoConfigs['/'];
  
  const finalTitle = title || config.title;
  const finalDescription = description || config.description;
  const finalKeywords = keywords || config.keywords;
  const canonicalUrl = canonical || `${BASE_URL}${path}`;

  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Nexus HR",
    "url": BASE_URL,
    "logo": `${BASE_URL}/lovable-uploads/a2e4d3de-cc00-4222-a473-67c9a76aed45.png`,
    "description": "Plataforma líder en reclutamiento empresarial que combina IA, psicometría y hunting",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Escazú",
      "addressRegion": "San José",
      "addressCountry": "CR"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+506-8886-2011",
      "contactType": "customer service",
      "email": "soporte@nexushrcr.com",
      "availableLanguage": ["Spanish", "English"]
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61575010856485",
      "https://www.instagram.com/nexushr.cr/",
      "https://www.linkedin.com/company/nexus-hr-costa-rica/"
    ]
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots & geo */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="geo.region" content="CR" />
      <meta name="geo.country" content="Costa Rica" />
      <meta httpEquiv="Content-Language" content="es" />

      {/* Open Graph tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Nexus HR" />
      <meta property="og:locale" content="es_CR" />
      
      {/* Twitter tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@NexusHR_CR" />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd || defaultJsonLd)}
      </script>
    </Helmet>
  );
};

export default SEOMetaTags;
